import TimeWheelPicker from 'components/ui/time-wheel-picker';
import { cloneElement, ReactElement, useEffect, useRef } from 'react';
import { TIMEOUT_DELAY } from 'constants/general';
import { t } from 'services/i18n.service';
import { useAppDispatch } from 'store/hooks';
import { runAfterDelay } from 'utils/helpers';

import { AnyAction } from '@reduxjs/toolkit';

interface TimeInputFieldProps {
  icon: ReactElement;
  labelKey: string;
  timeValue?: string;
  routineTimeValidation: {
    isValid: Record<string, boolean>;
    errorMessages: Record<string, string[]>;
  };
  updateAction: (value: string) => AnyAction;
  validationKey: string;
  defaultTime?: string;
  shouldHighlight?: boolean;
  onHighlightComplete?: () => void;
  dataTestId?: string;
}

const TimeInputField = ({
  icon,
  labelKey,
  timeValue,
  routineTimeValidation,
  updateAction,
  validationKey,
  defaultTime = '08:00',
  shouldHighlight = false,
  onHighlightComplete,
  dataTestId
}: TimeInputFieldProps) => {
  const dispatch = useAppDispatch();
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const { isValid, errorMessages } = routineTimeValidation;

  useEffect(() => {
    if (shouldHighlight && inputWrapperRef.current) {
      inputWrapperRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      const timer = runAfterDelay(() => {
        onHighlightComplete?.();
      }, TIMEOUT_DELAY.HIGHLIGHT_SHUTDOWN_TIME);

      return () => clearTimeout(timer);
    }
  }, [shouldHighlight, onHighlightComplete]);

  const handleTimeChange = (value?: string) => {
    dispatch(updateAction(value || ''));
  };

  const hasError = !isValid[validationKey];
  const errorMessagesArray = errorMessages[validationKey];
  const errorMessage =
    errorMessagesArray && errorMessagesArray.length > 0
      ? errorMessagesArray[0]
      : '';

  return (
    <div
      ref={inputWrapperRef}
      data-testid={dataTestId}
      className={`grid grid-cols-1 sm:grid-cols-8 py-2 ~gap-x-6/8 ~gap-y-2/4 align-middle transition-all duration-500 ${
        shouldHighlight
          ? '~mx-2/4 ~p-2/4 ring-2 ring-primary-500 dark:ring-primary-dark-600 ring-offset-2 ring-offset-secondary-50 dark:ring-offset-secondary-dark-100 rounded-lg bg-primary-50 dark:bg-primary-dark-50'
          : ''
      }`}>
      <h5 className='col-span-6 md:col-span-5 font-medium text-gray-900'>
        {cloneElement(icon, {
          className: '~w-4/6 ~h-4/6 inline-block ~mr-1.5/3 ~mb-0.5/1.5'
        })}
        {t(labelKey)}
      </h5>
      <div className='flex flex-col ~gap-1/1.5 md:col-span-3'>
        <TimeWheelPicker
          initialValue={
            timeValue && timeValue !== '00:00' ? timeValue : defaultTime
          }
          onChange={handleTimeChange}
          size='small'
        />
        {hasError && errorMessage && (
          <p className='text-primary-800 dark:text-primary-dark-500 text-xs'>
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default TimeInputField;
